import { API_TYPES } from "@src/appV2/api";
import {
  BAN_DISPUTE_REASON,
  BAN_DISPUTE_STATUS,
  PLACEMENT_CANDIDATE_BAN_ACTION,
} from "@src/appV2/PlacementCandidateBans/api/useFetchPaginatedPlacementCandidateBans";
import { z } from "zod";

// eslint-disable-next-line no-restricted-syntax
export enum InterviewStatus {
  BOOKED = "Booked",
  CANCELLED = "Cancelled",
  RESCHEDULED = "Rescheduled",
  ATTENDANCE_CONFIRMED = "AttendanceConfirmed",
  NO_CALL_NO_SHOW = "NoCallNoShow",
}

// eslint-disable-next-line no-restricted-syntax
export enum InterviewActionUserType {
  WORKER = "Worker",
  WORKPLACE = "Workplace",
  ADMIN = "Admin",
  SYSTEM = "System",
}

// eslint-disable-next-line no-restricted-syntax
export enum InterviewAttendanceMethod {
  GEOFENCE = "Geofence",
  MANUAL = "Manual",
  PROXIMITY = "Proximity",
}

// eslint-disable-next-line no-restricted-syntax
export enum InterviewType {
  PHONE_SCREEN = "phone-screen",
  IN_PERSON_INTERVIEW = "in-person-interview",
}

export const INTERVIEW_VERIFICATION_STATUS = {
  VERIFIED: "VERIFIED",
  UNVERIFIED: "UNVERIFIED",
} as const;
export type InterviewVerificationStatus =
  (typeof INTERVIEW_VERIFICATION_STATUS)[keyof typeof INTERVIEW_VERIFICATION_STATUS];

export const INTERVIEW_ATTENDANCE_VERIFICATION_STATUS = {
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
} as const;
export type InterviewAttendanceVerificationStatus =
  (typeof INTERVIEW_ATTENDANCE_VERIFICATION_STATUS)[keyof typeof INTERVIEW_ATTENDANCE_VERIFICATION_STATUS];

export const interviewDataSchema = z.object({
  type: z.literal(API_TYPES.interview),
  id: z.string(),
  attributes: z.object({
    start: z.string().datetime(),
    end: z.string().datetime(),
    status: z.nativeEnum(InterviewStatus),
    attendance: z
      .object({
        method: z.nativeEnum(InterviewAttendanceMethod).optional(),
        confirmedAt: z.string().datetime().optional(),
        confirmedBy: z.string().optional(),
      })
      .optional(),
    attendanceBonus: z
      .object({
        bonusId: z.string(),
        amount: z.object({
          amountInMinorUnits: z.number(),
          currencyCode: z.string(),
        }),
        createdAt: z.string().datetime(),
        createdBy: z.string().optional(),
        createdByUserType: z.nativeEnum(InterviewActionUserType).optional(),
      })
      .optional(),
    workerOutcome: z
      .object({
        dismissedAt: z.string().datetime().optional(),
        reminderSentAt: z.string().datetime().optional(),
        submittedAt: z.string().datetime().optional(),
        result: z.string().optional(),
      })
      .optional(),
    runningLate: z.object({ reportedAt: z.string().datetime() }).optional(),
    cancelledAt: z.string().datetime().optional(),
    verificationStatus: z.nativeEnum(INTERVIEW_VERIFICATION_STATUS).optional(),
    providerTripId: z.string().optional(),
  }),
  relationships: z.object({
    workplace: z.object({
      data: z.object({
        type: z.literal(API_TYPES.workplace),
        id: z.string(),
      }),
    }),
    worker: z.object({
      data: z.object({
        type: z.literal(API_TYPES.worker),
        id: z.string(),
      }),
    }),
    placementApplication: z.object({
      data: z.object({
        type: z.literal(API_TYPES.placementApplication),
        id: z.string(),
      }),
    }),
    placement: z.object({
      data: z.object({
        type: z.literal(API_TYPES.placement),
        id: z.string(),
      }),
    }),
    placementCandidate: z.object({
      data: z.object({
        type: z.literal(API_TYPES.placementCandidate),
        id: z.string(),
      }),
    }),
    rescheduledFrom: z
      .object({
        data: z.object({
          type: z.literal(API_TYPES.interview),
          id: z.string(),
        }),
      })
      .optional(),
    rescheduledTo: z
      .object({
        data: z.object({
          type: z.literal(API_TYPES.interview),
          id: z.string(),
        }),
      })
      .optional(),
    placementCandidateBan: z
      .object({
        data: z.object({
          type: z.literal(API_TYPES.placementCandidateBan),
          id: z.string(),
        }),
      })
      .optional(),
    interviewAttendanceVerification: z
      .object({
        data: z.object({
          type: z.literal(API_TYPES.interviewAttendanceVerification),
          id: z.string(),
        }),
      })
      .optional(),
  }),
});
export type PlacementApplicationData = z.infer<typeof interviewDataSchema>;

const interviewPlacementIncludeSchema = z.object({
  type: z.literal(API_TYPES.placement),
  id: z.string(),
  attributes: z.object({
    workerTypes: z.array(z.string()),
    title: z.string(),
  }),
});
export type InterviewIncludedPlacement = z.infer<typeof interviewPlacementIncludeSchema>;

const interviewWorkplaceIncludeSchema = z.object({
  type: z.literal(API_TYPES.workplace),
  id: z.string(),
  attributes: z.object({
    name: z.string(),
    formattedAddress: z.string().optional(),
    distance: z.number().optional(),
    phone: z.string().optional(),
    msa: z.string().optional(),
    tmz: z.string().optional(),
  }),
});
export type InterviewIncludedWorkplace = z.infer<typeof interviewWorkplaceIncludeSchema>;

const interviewWorkerIncludeSchema = z.object({
  type: z.literal(API_TYPES.worker),
  id: z.string(),
  attributes: z.object({
    name: z.string(),
    email: z.string(),
    phone: z.string(),
    tmz: z.string(),
    profileImageUrl: z.string().optional(),
  }),
});
export type InterviewIncludedWorker = z.infer<typeof interviewWorkerIncludeSchema>;

const interviewRescheduledIncludedSchema = z.object({
  type: z.literal(API_TYPES.interview),
  id: z.string(),
  attributes: z.object({
    start: z.string().datetime(),
    end: z.string().datetime(),
    status: z.nativeEnum(InterviewStatus),
  }),
});

export type InterviewScheduledIncludedSchema = z.infer<typeof interviewRescheduledIncludedSchema>;

const interviewPlacementCandidateIncludedSchema = z.object({
  type: z.literal(API_TYPES.placementCandidate),
  id: z.string(),
  attributes: z.object({
    accessId: z.string(),
  }),
});
export type InterviewPlacementCandidateIncluded = z.infer<
  typeof interviewPlacementCandidateIncludedSchema
>;

const interviewPlacementCandidateBanIncludedSchema = z.object({
  type: z.literal(API_TYPES.placementCandidateBan),
  id: z.string(),
  attributes: z.object({
    reason: z.string(),
    action: z.nativeEnum(PLACEMENT_CANDIDATE_BAN_ACTION).optional(),
    revokedAt: z.coerce.date().optional(),
    startsAt: z.coerce.date(),
    expiresAt: z.coerce.date().optional(),
    dispute: z
      .object({
        status: z.nativeEnum(BAN_DISPUTE_STATUS),
        reason: z.nativeEnum(BAN_DISPUTE_REASON),
        description: z.string(),
      })
      .optional(),
  }),
});
export type InterviewPlacementCandidateBanIncluded = z.infer<
  typeof interviewPlacementCandidateBanIncludedSchema
>;

const interviewIncludedInterviewAttendanceVerificationSchema = z.object({
  type: z.literal(API_TYPES.interviewAttendanceVerification),
  id: z.string(),
  attributes: z.object({
    status: z.nativeEnum(INTERVIEW_ATTENDANCE_VERIFICATION_STATUS),
    interviewerName: z.string(),
    attendanceDescription: z.string(),
    hasEvidenceFile: z.boolean(),
    resolvedAt: z.string().datetime().optional(),
    createdAt: z.string().datetime(),
  }),
});
export type InterviewIncludedInterviewAttendanceVerification = z.infer<
  typeof interviewIncludedInterviewAttendanceVerificationSchema
>;

export const interviewResponseSchema = z.object({
  data: interviewDataSchema,
  included: z.array(
    z.union([
      interviewPlacementIncludeSchema,
      interviewWorkplaceIncludeSchema,
      interviewWorkerIncludeSchema,
      interviewRescheduledIncludedSchema,
      interviewPlacementCandidateIncludedSchema,
      interviewPlacementCandidateBanIncludedSchema,
      interviewIncludedInterviewAttendanceVerificationSchema,
    ])
  ),
});
export type InterviewResponse = z.infer<typeof interviewResponseSchema>;

export const interviewsResponseSchema = z.object({
  data: z.array(interviewResponseSchema.shape.data),
  included: interviewResponseSchema.shape.included,
  links: z.object({
    nextCursor: z.string().optional(),
  }),
});
export type InterviewsResponse = z.infer<typeof interviewsResponseSchema>;
